import { usePlacements } from "../../api/queries";
import type { Placement } from "../../api/types";
import { StatePill, type PillShape, type PillTone } from "../../components/StatePill";
import "./services.css";

interface PlacementsSummaryCellProps {
  projectId: string;
  serviceId: string;
}

/** Worst-first: one failure anywhere reads as trouble even if everything
 * else is running; mid-flight beats a stale "all good" read while a deploy
 * is still landing. Exported so any other placement-rollup surface reads the
 * identical semantics — the same count of placements should never summarize
 * differently depending on where you view it from. */
export function aggregatePlacements(placements: Placement[]): { label: string; tone: PillTone; shape: PillShape } {
  if (placements.length === 0) return { label: "Not running", tone: "neutral", shape: "ring" };
  if (placements.some((p) => p.status === "failed")) return { label: "Failed", tone: "negative", shape: "triangle" };
  if (placements.some((p) => p.status === "pending" || p.status === "deploying")) {
    return { label: "Deploying", tone: "info", shape: "spin" };
  }
  if (placements.every((p) => p.status === "running")) return { label: "Running", tone: "positive", shape: "dot" };
  return { label: "Mixed", tone: "warning", shape: "bars" };
}

/**
 * The project services table's "Where it runs" column: a count plus one
 * aggregated status dot (ProjectDetailPage, PR P1.6b) — each row runs its
 * own bounded `usePlacements` fetch (one small page per service, matching
 * the picker precedent in PlacementsSection) rather than requiring a
 * project-wide placements endpoint that doesn't exist yet.
 */
export function PlacementsSummaryCell({ projectId, serviceId }: PlacementsSummaryCellProps) {
  const { data, isPending } = usePlacements(projectId, serviceId, { limit: 50 });

  if (isPending) return <span className="data-table__secondary">Loading…</span>;

  const items = data?.items ?? [];
  const meta = aggregatePlacements(items);

  return (
    <span className="placements-summary-cell">
      <span className="data-table__mono">{items.length}</span>
      <StatePill label={meta.label} tone={meta.tone} shape={meta.shape} />
    </span>
  );
}
